import { Grid, GridColumn as Column, GridColumnMenuCheckboxFilter, GridCellProps, GridItemChangeEvent, GridToolbar, GridColumnResizeEvent, GridDataStateChangeEvent, GridColumnMenuFilter, GridNoRecords } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { MyCommandCell } from '../components/synoptic/MyCommandCell';
import { AccessToken } from '../config/AccessToken';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';

import { process as kendoProcess } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import { arrowRotateCwIcon, fileCsvIcon, fileExcelIcon, filePdfIcon, saveIcon, uploadIcon, warningTriangleIcon } from '@progress/kendo-svg-icons';
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { loadingPanel } from '../components/LoadingPanel';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { SvgIcon } from '@progress/kendo-react-common';
import { Switch } from '@progress/kendo-react-inputs';
import { alarm } from '../assets/alarm';
import { EditDescriptor, HeaderThElement } from '@progress/kendo-react-data-tools';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { NotificationGroup, Notification } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { getSharedMqttClient, isMqttConnectionEnabled, parseMqttMessage, setMqttConnectionEnabled as setGlobalMqttConnectionEnabled } from '../config/mqttClient';
import { fetchServerSettings } from '../config/serverSettings';

/**
 * SynopticDevices component manages and displays a grid of device information.
 * It includes functionalities for device list retrieval, filtering, sorting,
 * column management, and exporting data to Excel, PDF, and CSV formats.
 *
 * The component interacts with a server to fetch device data, allows editing
 * device names, and provides a UI for toggling column visibility. It also 
 * supports pagination and responsive design adjustments based on grid width.
 *
 * State management is used extensively to manage devices, columns, data states,
 * and UI states such as loading and error handling.
 *
 * Utilizes KendoReact components for grid and toolbar functionalities, and
 * React's useEffect hook to initialize device data on component mount.
 */

const editField = "inEdit";

const safeParseJSON = <T,>(rawValue: string | null, fallback: T): T => {
  if (!rawValue) return fallback;
  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
};

const getOnlyBoundFlag = (): boolean => {
  return safeParseJSON<boolean>(localStorage.getItem('onlyBound'), false);
};

const getLiveStatusFlag = (): boolean => {
  return safeParseJSON<boolean>(localStorage.getItem('liveStatus'), false);
};

const getApiErrorMessage = (error: any, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }
    if (typeof responseData?.error === 'string' && responseData.error.trim()) {
      return responseData.error;
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return fallback;
};

const EditingCell = (props: any) => {
  const { edit, update, cancel, editField } = props;

  return (
    <MyCommandCell
      {...props}
      edit={edit}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );
};

// Custom cell for name editing that preserves cursor position
const NameEditCell = (props: GridCellProps) => {
  const { dataItem, field } = props;
  const [localValue, setLocalValue] = useState(dataItem[field || '']);
  const isInEdit = dataItem[editField];

  // Update local value when dataItem changes (e.g., when entering edit mode)
  useEffect(() => {
    if (isInEdit) {
      setLocalValue(dataItem[field || '']);
    }
  }, [isInEdit, dataItem, field]);

  if (!isInEdit) {
    return <td>{dataItem[field || '']}</td>;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    // Update the dataItem only after a small delay to prevent continuous re-renders
    dataItem[field || ''] = e.target.value;
  };

  return (
    <td>
      <input
        className="k-textbox"
        type="text"
        value={localValue || ''}
        onChange={handleChange}
        style={{ width: '100%' }}
        autoFocus
      />
    </td>
  );
};

const LinkMapCell = (props: any) => {

  const navigate = useNavigate()

  const localizationService = useLocalization();
  let lastPositionDate = props?.dataItem?.lastPositionDate;
  let deviceMapId = props?.dataItem?.mapId;
  //let deviceGroupId = props?.dataItem?.groupId;
  let deviceMac = props?.dataItem?.mac;

  const showMap = () => {

    if (deviceMapId != null) {
      localStorage.setItem("deviceMapIdFromSynoptic", deviceMapId);
    }

    if (deviceMac != null) {
      localStorage.setItem("deviceMacFromSynoptic", deviceMac);
    }

    localStorage.removeItem("beaconId")
    localStorage.removeItem("mapIdByBeacon")
    localStorage.removeItem("beaconFromGrid")

    navigate("/home/map")
  }

  const isMapEnabled = deviceMapId != null && lastPositionDate != 0 && lastPositionDate != '-';
  const mapButtonTitle =
    deviceMapId != null && (lastPositionDate != 0 && lastPositionDate != '-')
      ? localizationService.toLanguageString('synoptic_devices.map', 'NOT_FOUND')
      : localizationService.toLanguageString('synoptic_devices.no_position_found', 'NOT_FOUND');

  return (
    <td className="k-command-cell" style={{ textAlign: 'center' }}>
      <button
        type="button"
        title={mapButtonTitle}
        aria-label={mapButtonTitle}
        className="k-icon k-font-icon k-i-globe synoptic-globe synoptic-cell-action"
        style={{
          color: '#0f6671',
          margin: '6px',
          padding: '12px',
          borderRadius: '999px',
          border: 'none',
          background: '#e8f5f7',
          outline: 'none',
          cursor: isMapEnabled ? 'pointer' : 'not-allowed',
          opacity: isMapEnabled ? 1 : 0.4,
          boxShadow: isMapEnabled ? '0 2px 8px rgba(11, 74, 84, 0.2)' : 'none'
        }}
        disabled={!isMapEnabled}
        onClick={isMapEnabled ? showMap : undefined}
      />
    </td>

  );
};

const LinkDeviceManagement = (props: any) => {

  const localizationService = useLocalization();
  const navigate = useNavigate()
  const userType = localStorage.getItem('userType');
  const isStandardUser = userType?.startsWith('Standard') ?? false;
  let deviceMac: any = props?.dataItem?.mac;
  const deviceManagementTitle = localizationService.toLanguageString('configuration.devices.devices_management', 'NOT_FOUND');

  return (
    <td className="k-command-cell" style={{ textAlign: 'center', opacity: isStandardUser ? 0.5 : 1, pointerEvents: isStandardUser ? 'none' : 'auto' }}>
      <button
        type="button"
        //id='deviceManagement'
        title={deviceManagementTitle}
        aria-label={deviceManagementTitle}
        className="k-icon k-font-icon k-i-gears synoptic-cell-action"
        style={{
          color: '#0f6671',
          margin: '6px',
          padding: '12px',
          borderRadius: '999px',
          border: 'none',
          background: '#e8f5f7',
          outline: 'none',
          boxShadow: '0 2px 8px rgba(11, 74, 84, 0.2)',
          cursor: 'pointer'
        }}
        onClick={() => navigate("/home/configuration/devices/devices-management", { state: { deviceMac: deviceMac } })}
      >
        <a
          id='deviceManagement'
          aria-hidden='true'
          title={deviceManagementTitle}
        />
      </button>
    </td>
  );
};

export const SynopticDevices = () => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const mqttNotificationIdRef = useRef(0);

  const [edit, setEdit] = useState<EditDescriptor>({});

  const [columns, setColumns] = useState<any>([]);

  const [devices, setDevices] = useState<any>([]);
  const [devicesOn, setDevicesOn] = useState<any>([]);
  const [countDevicesOn, setCountDevicesOn] = useState(0);
  const devicesRef = useRef<any[]>([]);
  const devicesOnRef = useRef<any[]>([]);

  const [error, setError] = useState('');
  const [mqttNotifications, setMqttNotifications] = useState<Array<{ id: number; message: string; style: 'info' | 'warning' | 'error' | 'success' }>>([]);
  const [mqttClientRetryKey, setMqttClientRetryKey] = useState(0);
  const serverSettingsCacheRef = useRef<any[] | null>(null);
  const inFlightServerSettingsRequestRef = useRef<Promise<any[] | null> | null>(null);

  const [loading, setLoading] = useState(false);

  const localizationService = useLocalization();
  const [instance, accessTokenRequest] = AccessToken();

  const userEmail = localStorage.getItem('email');
  const statusDevice = Number(localStorage.getItem('statusDevice')) || 0;

  const [pageSizeValue, setPageSizeValue] = useState<number | string | undefined>()

  const [liveStatus, setLiveStatus] = useState<boolean>(() => getLiveStatusFlag());
  const [onlyBound, setOnlyBound] = useState<boolean>(() => getOnlyBoundFlag());
  const [mqttConnectionEnabled, setMqttConnectionEnabledState] = useState<boolean>(() => isMqttConnectionEnabled());

  const [firstLoad, setFirstLoad] = useState(true);
  const [visibleCount, setVisibleCount] = useState(0);

  const [filterName, setFilterName] = useState<any>(() => {
    return safeParseJSON<string[]>(localStorage.getItem('filterNameSynoptic'), []);
  });

  const [editMode, setEditMode] = useState<boolean>(false);
  const editModeRef = useRef(false);
  const isMountedRef = useRef(true);

  const refreshAPI = Number(localStorage.getItem('refreshAPI') ?? 30);

  let languageId: any = localStorage.getItem('languageId');

  const visibleCountSynoptic = safeParseJSON<number>(localStorage.getItem("visibleCountSynoptic"), 0);

  const userType = localStorage.getItem('userType');

  const pushMqttNotification = (message: string, style: 'info' | 'warning' | 'error' | 'success' = 'info') => {
    const nextId = ++mqttNotificationIdRef.current;
    setMqttNotifications((prev) => [...prev, { id: nextId, message, style }]);

    setTimeout(() => {
      setMqttNotifications((prev) => prev.filter((item) => item.id !== nextId));
    }, 30000);
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  useEffect(() => {
    devicesRef.current = Array.isArray(devices) ? devices : [];
  }, [devices]);

  useEffect(() => {
    devicesOnRef.current = Array.isArray(devicesOn) ? devicesOn : [];
  }, [devicesOn]);

  const shouldPauseIntervalRefresh = () => {
    const filteredColumn = localStorage.getItem('filterOpenedColumn');
    return filteredColumn === 'openedColumn' && editModeRef.current;
  };

  const shouldPauseMqttRefresh = () => {
    const filteredColumn = localStorage.getItem('filterOpenedColumn');
    return filteredColumn === 'openedColumn' || editModeRef.current;
  };

  useEffect(() => {
    if (mqttConnectionEnabled) {
      return;
    }

    const refreshIntervalSeconds = refreshAPI;
    if (!Number.isFinite(refreshIntervalSeconds) || refreshIntervalSeconds <= 0) {
      return;
    }

    // Set a timeout to call getDeviceList with delay based on refreshAPI
    const intervalId = setInterval(() => {
      if (!shouldPauseIntervalRefresh()) {
        const onlyBoundRaw = localStorage.getItem('onlyBound');
        const onlyBoundValue = onlyBoundRaw === null ? undefined : onlyBoundRaw === 'true';
        getDeviceList(onlyBoundValue);
      }
    }, refreshIntervalSeconds * 1000); // Delay in milliseconds

    return () => clearInterval(intervalId);
  }, [editMode, refreshAPI, mqttConnectionEnabled]);

  
  useEffect(() => {
    if (!mqttConnectionEnabled) {
      return;
    }

    const client = getSharedMqttClient(SERVER_URL);
    if (!client) {
      const retryTimer = window.setTimeout(() => {
        setMqttClientRetryKey((prev) => prev + 1);
      }, 1500);

      return () => {
        window.clearTimeout(retryTimer);
      };
    }

    let isDisposed = false;
    let wakeupTopic = '';

    const mqttTopicMatches = (topic: string, filter: string) => {
      if (!filter) {
        return false;
      }

      const topicLevels = topic.split('/');
      const filterLevels = filter.split('/');

      for (let i = 0; i < filterLevels.length; i++) {
        const filterLevel = filterLevels[i];
        const topicLevel = topicLevels[i];

        if (filterLevel === '#') {
          return true;
        }

        if (topicLevel === undefined) {
          return false;
        }

        if (filterLevel === '+' || filterLevel === '*') {
          continue;
        }

        if (filterLevel !== topicLevel) {
          return false;
        }
      }

      return topicLevels.length === filterLevels.length;
    };

    const applyWakeupTopic = (nextTopicRaw: any) => {
      const nextTopic = typeof nextTopicRaw === 'string' ? nextTopicRaw.trim() : '';
      if (nextTopic === wakeupTopic) {
        return;
      }

      const previousTopic = wakeupTopic;
      wakeupTopic = nextTopic;

      if (previousTopic) {
        client.unsubscribe(previousTopic);
      }

      if (wakeupTopic && client.connected) {
        client.subscribe(wakeupTopic);
      }
    };

    const resolveWakeupTopicFromSettings = async () => {
      const applySettings = (settings: any[]) => {
        const wakeupTopicSetting = settings?.find((setting: any) => setting.id == 'MqttWebSocket_WakeupTopic')?.value;
        applyWakeupTopic(wakeupTopicSetting);
      };

      const loadServerSettings = async (): Promise<any[] | null> => {
        if (serverSettingsCacheRef.current) {
          return serverSettingsCacheRef.current;
        }

        if (inFlightServerSettingsRequestRef.current) {
          return inFlightServerSettingsRequestRef.current;
        }

        const request = (async () => {
          const fetchWithToken = async (accessToken: any): Promise<any[] | null> => {
            const currentLanguageId = localStorage.getItem('languageId');
            return fetchServerSettings(SERVER_URL, accessToken, currentLanguageId);
          };

          const acquireFreshAccessToken = async (): Promise<string | null> => {
            await instance.initialize();

            const account = accessTokenRequest?.account
              ?? instance.getActiveAccount()
              ?? instance.getAllAccounts()?.[0];

            if (!account) {
              return null;
            }

            const activeAccount = instance.getActiveAccount();
            if (!activeAccount || activeAccount.homeAccountId !== account.homeAccountId) {
              instance.setActiveAccount(account);
            }

            const tokenRequest: any = {
              ...accessTokenRequest,
              account,
            };

            const accessTokenResponse: any = await instance.acquireTokenSilent(tokenRequest);
            const nextAccessToken = accessTokenResponse?.accessToken ?? null;

            if (nextAccessToken) {
              localStorage.setItem('accessToken', nextAccessToken);
            }

            return nextAccessToken;
          };

          try {
            const storedAccessToken = localStorage.getItem('accessToken');
            let settings = storedAccessToken != null
              ? await fetchWithToken(storedAccessToken)
              : null;

            if (!settings) {
              const refreshedAccessToken = await acquireFreshAccessToken();
              if (refreshedAccessToken) {
                settings = await fetchWithToken(refreshedAccessToken);
              }
            }

            serverSettingsCacheRef.current = settings;
            return settings;
          } catch (error: any) {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }

            return null;
          }
        })();

        inFlightServerSettingsRequestRef.current = request;
        request.finally(() => {
          inFlightServerSettingsRequestRef.current = null;
        });

        return request;
      };

      const settings = await loadServerSettings();
      if (!settings) {
        applyWakeupTopic('');
        return;
      }

      applySettings(settings);
    };

    const onWakeupTopicChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ topic?: string }>;
      applyWakeupTopic(customEvent?.detail?.topic ?? '');
    };

    const onConnect = async () => {
      if (!wakeupTopic) {
        await resolveWakeupTopicFromSettings();
      }

      if (isDisposed || !wakeupTopic) {
        return;
      }

      client.subscribe(wakeupTopic);
    };

    const onMessage = (topic: string, message: Buffer) => {
      if (shouldPauseMqttRefresh()) {
        return;
      }

      const rawMessage = message.toString();
      let parsedText: any = null;
      try {
        parsedText = JSON.parse(parseMqttMessage(rawMessage));
      } catch {
        parsedText = null;
      }

      if (parsedText) {
        updateGridFromIncomingDeviceJson(parsedText);
      }

      if (parsedText && wakeupTopic && mqttTopicMatches(topic, wakeupTopic)) {

        const groupsInfoRaw = localStorage.getItem('groupsInfo');
        let groupsInfo: any[] = [];

        if (groupsInfoRaw) {
          try {
            groupsInfo = JSON.parse(groupsInfoRaw);
          } catch {
            groupsInfo = [];
          }
        }

        const currentDevice = devicesOn.find((device: any) => Number(device?.mac) === Number(parsedText?.DeviceMac));
        const groupId = parsedText?.GroupId;
        const currentGroup = groupsInfo.find((group: any) => Number(group?.id) === Number(groupId));


        const deviceMac = parsedText?.DeviceMac ?? parsedText?.deviceMac ?? '';
        const deviceName = parsedText?.Name ?? parsedText?.name ?? '';
        const serialNumber = parsedText?.SerialNumber ?? parsedText?.serialNumber ?? '';
        const groupName = currentGroup?.name ??  '';
        const jobName = currentDevice?.jobName ?? '';
        const zoneName = currentDevice?.zoneNameListJoined ?? '';
        const deviceTypeName = currentDevice?.DeviceTypeName ?? '';
        const temperature = parsedText?.Temperature != null ? `${parsedText.Temperature} °C` : '';

        const synopticMessage: any = (
          <div>
            <div>Wakeup device_mac:{deviceMac}</div>
            <div>{deviceName}</div>
            <div>{serialNumber}</div>
            <div>{groupName}</div>
            <div>{jobName}</div>
            <div>{zoneName}</div>
            <div>{deviceTypeName}</div>
            <div>{temperature}</div>
          </div>
        );
        pushMqttNotification(synopticMessage, 'info');
      }

    };

    client.on('connect', onConnect);
    client.on('message', onMessage);
    window.addEventListener('settings:mqtt-wakeup-topic-changed', onWakeupTopicChanged as EventListener);

    if (client.connected) {
      onConnect();
    }

    return () => {
      isDisposed = true;
      if (wakeupTopic) {
        client.unsubscribe(wakeupTopic);
      }
      client.off('connect', onConnect);
      client.off('message', onMessage);
      window.removeEventListener('settings:mqtt-wakeup-topic-changed', onWakeupTopicChanged as EventListener);
    };
  }, [SERVER_URL, instance, accessTokenRequest, mqttConnectionEnabled, mqttClientRetryKey]);


  const formatDateFromIsoToString = (date: any) => {

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (languageId == 'en-US') {
      hours = hours % 12;
      hours = hours === 0 ? 12 : hours; // convert 0 → 12
      const hours12 = String(hours).padStart(2, '0');
      return `${month}/${day}/${year} ${hours12}:${minutes}:${seconds}`;
    } else {
      const hours24 = String(hours).padStart(2, '0');
      return `${day}/${month}/${year} ${hours24}:${minutes}:${seconds}`;
    }
  }

  const convertDateTime = (dateTime: any, treatMissingTimezoneAsUtc: boolean = false) => {

    if (dateTime == null) {
      return '-'
    }

    if (dateTime instanceof Date) {
      return dateTime;
    }

    if (typeof dateTime !== 'string') {
      return '-'
    }

    const normalizedDateTime = dateTime.trim();
    const timezoneSuffixPattern = /(?:Z|[+\-]\d{2}:\d{2})$/i;
    const isoLikeDateTimePattern = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?)?$/;
    const shouldAssumeUtcIso = treatMissingTimezoneAsUtc
      && isoLikeDateTimePattern.test(normalizedDateTime)
      && !timezoneSuffixPattern.test(normalizedDateTime);
    const normalizedIsoDateTime = shouldAssumeUtcIso
      ? `${normalizedDateTime.replace(' ', 'T')}Z`
      : normalizedDateTime;
    const parsedIsoDate = new Date(normalizedIsoDateTime);

    if (!Number.isNaN(parsedIsoDate.getTime())) {
      return parsedIsoDate;
    }

    const legacyMatch = normalizedDateTime.match(
      /^(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
    );

    if (!legacyMatch) {
      return '-'
    }

    const [, part1, part2, part3, hourPart = '0', minutePart = '0', secondPart = '0', millisecondPart = '0'] = legacyMatch;
    const first = Number(part1);
    const second = Number(part2);
    const third = Number(part3);
    const hour = Number(hourPart);
    const minute = Number(minutePart);
    const seconds = Number(secondPart);
    const milliseconds = Number(millisecondPart.slice(0, 3).padEnd(3, '0'));

    if ([first, second, third, hour, minute, seconds, milliseconds].some((value) => Number.isNaN(value))) {
      return '-'
    }

    const isYearFirst = String(part1).length === 4;
    const year = isYearFirst ? first : third;
    const month = second;
    const day = isYearFirst ? third : first;
    const parsedLegacyDate = treatMissingTimezoneAsUtc
      ? new Date(Date.UTC(year, month - 1, day, hour, minute, seconds, milliseconds))
      : new Date(year, month - 1, day, hour, minute, seconds, milliseconds);

    return Number.isNaN(parsedLegacyDate.getTime()) ? '-' : parsedLegacyDate
  }

  const normalizeMac = (value: any): string => {
    return String(value ?? '').trim().toLowerCase();
  }

  const readIncomingField = (payload: any, primaryField: string, fallbackField?: string) => {
    if (payload?.[primaryField] !== undefined) {
      return payload[primaryField];
    }

    if (fallbackField && payload?.[fallbackField] !== undefined) {
      return payload[fallbackField];
    }

    return undefined;
  }

  const updateGridFromIncomingDeviceJson = (payload: any): boolean => {
    const payloadMac = normalizeMac(readIncomingField(payload, 'mac', 'DeviceMac'));
    if (!payloadMac) {
      return false;
    }

    const applyIncomingPatch = (device: any) => {
      if (normalizeMac(device?.mac) !== payloadMac) {
        return device;
      }

      const updatedDevice = { ...device };

      const serialNumber = readIncomingField(payload, 'serialNumber', 'SerialNumber');
      const name = readIncomingField(payload, 'name', 'Name');
      const deviceTypeName = readIncomingField(payload, 'deviceTypeName', 'DeviceTypeName');
      const battery = readIncomingField(payload, 'battery', 'Battery');
      const temperature = readIncomingField(payload, 'temperature', 'Temperature');
      const isEnabled = readIncomingField(payload, 'isEnabled', 'IsEnabled');
      const mapId = readIncomingField(payload, 'mapId', 'MapId');
      const inCharge = readIncomingField(payload, 'inCharge', 'InCharge');
      const customInfo = readIncomingField(payload, 'customInfo', 'CustomInfo');
      const customInfoDictionary = readIncomingField(payload, 'customInfoDictionary', 'CustomInfoDictionary');
      const groupId = readIncomingField(payload, 'groupId', 'GroupId');
      const groupName = readIncomingField(payload, 'groupName', 'GroupName');
      const groupColor = readIncomingField(payload, 'groupColor', 'GroupColor');
      const jobId = readIncomingField(payload, 'jobId', 'JobId');
      const jobName = readIncomingField(payload, 'jobName', 'JobName');
      const jobColor = readIncomingField(payload, 'jobColor', 'JobColor');
      const hasAlarm = readIncomingField(payload, 'hasAlarm', 'HasAlarm');
      const zoneNameListJoined = readIncomingField(payload, 'zoneNameListJoined', 'ZoneNameListJoined');
      const technology = readIncomingField(payload, 'technology', 'Technology');
      const nfc = readIncomingField(payload, 'nfc', 'Nfc');
      const ip = readIncomingField(payload, 'ip', 'Ip');
      const localTimeRaw = readIncomingField(payload, 'localTime', 'LocalTime');
      const dateTimeRaw = readIncomingField(payload, 'dateTime', 'Datetime');
      const localLastPositionDateRaw = readIncomingField(payload, 'localLastPositionDate', 'LocalLastPositionDate');
      const lastPositionDateRaw = readIncomingField(payload, 'lastPositionDate', 'LastPositionDate');
      const mac = readIncomingField(payload, 'mac', 'DeviceMac');
      const version = readIncomingField(payload, 'version', 'Version');

      if (mac !== undefined) updatedDevice.mac = mac;
      if (serialNumber !== undefined) updatedDevice.serialNumber = serialNumber;
      if (name !== undefined) updatedDevice.name = name;
      if (deviceTypeName !== undefined) updatedDevice.deviceTypeName = deviceTypeName;
      if (version !== undefined) updatedDevice.version = version;
      if (battery !== undefined) updatedDevice.battery = battery;
      if (temperature !== undefined) updatedDevice.temperature = temperature;
      if (isEnabled !== undefined) updatedDevice.isEnabled = isEnabled;
      if (mapId !== undefined) updatedDevice.mapId = mapId;
      if (inCharge !== undefined) updatedDevice.inCharge = inCharge;
      if (customInfo !== undefined) updatedDevice.customInfo = customInfo;
      if (customInfoDictionary !== undefined) updatedDevice.customInfoDictionary = customInfoDictionary;
      if (groupId !== undefined) updatedDevice.groupId = groupId;
      if (groupName !== undefined) updatedDevice.groupName = groupName;
      if (groupColor !== undefined) updatedDevice.groupColor = groupColor;
      if (jobId !== undefined) updatedDevice.jobId = jobId;
      if (jobName !== undefined) updatedDevice.jobName = jobName;
      if (jobColor !== undefined) updatedDevice.jobColor = jobColor;
      if (hasAlarm !== undefined) updatedDevice.hasAlarm = hasAlarm;
      if (zoneNameListJoined !== undefined) updatedDevice.zoneNameListJoined = zoneNameListJoined;
      if (technology !== undefined) updatedDevice.technology = technology;
      if (nfc !== undefined) updatedDevice.nfc = nfc;
      if (ip !== undefined) updatedDevice.ip = ip;

      if (localTimeRaw !== undefined) {
        updatedDevice.localTime = convertDateTime(localTimeRaw);
      }

      if (dateTimeRaw !== undefined) {
        const convertedDate = convertDateTime(dateTimeRaw);
        updatedDevice.dateTime = convertedDate;

        if (convertedDate instanceof Date) {
          const onlineThreshold = Date.now() - (statusDevice * 60 * 1000);
          updatedDevice.status = convertedDate.getTime() > onlineThreshold ? 1 : 0;
        }
      }

      if (localLastPositionDateRaw !== undefined) {
        updatedDevice.localLastPositionDate = convertDateTime(localLastPositionDateRaw);
      }

      if (lastPositionDateRaw !== undefined) {
        updatedDevice.lastPositionDate = convertDateTime(lastPositionDateRaw);
      }

      return updatedDevice;
    };

    const upsertByMac = (items: any[]) => {
      const currentItems = Array.isArray(items) ? items : [];
      const targetIndex = currentItems.findIndex((device: any) => normalizeMac(device?.mac) === payloadMac);

      if (targetIndex === -1) {
        const newDevice = applyIncomingPatch({
          mac: payloadMac,
          isEnabled: true,
          status: 0,
          hasAlarm: false,
          customInfo: '',
          customInfoDictionary: null
        });

        return [...currentItems, newDevice];
      }

      return currentItems.map(applyIncomingPatch);
    };

    setDevices((prevDevices: any[]) => upsertByMac(prevDevices));
    setDevicesOn((prevDevicesOn: any[]) => upsertByMac(prevDevicesOn));
    return true;
  }

  const getDeviceList = async (onlyBound?: boolean) => {
    let languageId = localStorage.getItem('languageId');
    const apiErrorMessage = localizationService.toLanguageString(
      'synoptic_devices.load_error',
      languageId === 'it-IT' ? 'Errore durante il caricamento dei dispositivi' : 'Unable to load devices'
    );

    if (isMountedRef.current) {
      setError('');
      setLoading(true)
    }

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      try {
        const res = await axios.get(`${SERVER_URL}/device/list`, { headers });
        const data = res?.data?.data ?? [];

        const currentOnlyBound = localStorage.getItem('onlyBound') ?? onlyBound;
        const liveStatus = localStorage.getItem('liveStatus') === 'true';
        const baseDateRange = getDateRange(statusDevice);

        let deviceOn: any[] = [];

        for (const device of data) {
          if (!device?.isEnabled) continue;

          // --- Normalizzazione ---
          ['temperature', 'battery'].forEach((key) => {
            if (device[key] != null) device[key] = device[key];
          });

          ['dateTime', 'localTime', 'lastPositionDate', 'localLastPositionDate'].forEach((key) => {
            device[key] = convertDateTime(device[key], true);
          });

          const localTimeString = device.dateTime?.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });

          // --- Calcolo stato ---
          device.status =
            device.dateTime == 0 || compareDateTimes(localTimeString, baseDateRange) !== 1 ? 0 : 1;

          const hasName = !!device?.name;

          // --- LOGICA PRINCIPALE ---
          if (currentOnlyBound === 'true' && liveStatus) {
            // ✅ Se è abilitato e "onlyBound" è true con live attivo
            if (!hasName) {
              continue;
            }

            // Verifica temporale
            const dateBeforeMinutes = getDateRange(statusDevice);
            const isRecent = compareDateTimes(localTimeString, dateBeforeMinutes) === 1;

            if (isRecent) deviceOn.push(device);
          }
          else if (liveStatus && (currentOnlyBound === 'false' || !currentOnlyBound)) {
            // ✅ Se liveStatus è true ma onlyBound è false o non impostato
            if (!device.dateTime || device.dateTime === 0) continue;

            const dateBeforeMinutes = getDateRange(statusDevice);
            const isRecent = compareDateTimes(localTimeString, dateBeforeMinutes) === 1;

            if (isRecent) deviceOn.push(device);
          }
          else {
            // ✅ Caso liveStatus = false
            if (currentOnlyBound === 'true' && !hasName) continue;
            deviceOn.push(device);
          }
        }

        // --- Aggiornamento stati ---
        // Preserva lo stato inEdit dei dispositivi in modifica
        if (!isMountedRef.current) {
          return;
        }

        setDevicesOn((prevDevices: any) => {
          return deviceOn.map((device: any) => {
            const prevDevice = prevDevices.find((d: any) => d.mac === device.mac);
            if (prevDevice?.inEdit) {
              return { ...device, inEdit: true, name: prevDevice.name }; // Mantieni inEdit e il nome in modifica
            }
            return device;
          });
        });
        setCountDevicesOn(deviceOn.length);
        setDevices(deviceOn);

        // --- Pulizia customInfo ---
        for (const device of deviceOn) {
          if (!device.customInfo) continue;
          try {
            const customInfo = JSON.parse(device.customInfo);
            delete customInfo.ProximitySensor;
            delete customInfo.SensorProximity;
            device.customInfo = JSON.stringify(customInfo);
          } catch {
            // Ignora JSON non valido
          }
        }

        if (!isMountedRef.current) {
          return;
        }

        loadStateFromLocalStorage();
        setLoading(false);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
          return;
        }
        console.error('Errore durante il caricamento dei dispositivi:', error);
        if (isMountedRef.current) {
          setDevices([]);
          setDevicesOn([]);
          setCountDevicesOn(0);
          setError(apiErrorMessage);
          // Keep column/header configuration available even when the API fails.
          loadStateFromLocalStorage();
          setLoading(false);
        }
      }



    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance?.initialize();
      await instance?.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse?.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  useEffect(() => {
    loadStateFromLocalStorage()
    getDeviceList()
  }, [])

  const editFieldName = async (deviceMac: any, deviceName: any, deviceGroupId: any, deviceJobId: any, startDate: any, deviceSerialNumber: any) => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      let params = {}
      if (deviceGroupId != 0) {
        params = {
          deviceMac,
          name: deviceName,
          groupId: deviceGroupId,
          jobId: deviceJobId,
          serial: deviceSerialNumber
        }
      } else {
        params = {
          deviceMac,
          name: deviceName,
          serial: deviceSerialNumber
        }
      }

      try {
        await axios.put(`${SERVER_URL}/device/${deviceMac}`, params, { headers })

        const onlyBound: any = safeParseJSON<boolean>(localStorage.getItem('onlyBound'), false)
        if (onlyBound) {
          setOnlyBound(onlyBound)
          if (liveStatus) {
            onToggleLive(liveStatus)
          } else {
            getDeviceList(onlyBound)
          }
        } else {
          getDeviceList()
        }

      } catch (error: any) {
        if (isMountedRef.current) {
          setError(getApiErrorMessage(error, localizationService.toLanguageString('synoptic_devices.no_device_found', 'Nessun dispositivo presente')));
        }
      }
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const IsUnaryFilter = (operator: any) => {
    return ['isnull', 'isnotnull', 'isempty', 'isnotempty'].includes(operator);
  };

  const cellOperatorChange = (event: any, currentValue: any, onChange: any) => {
    let value = currentValue;
    const item = event.target.value;
    if (!event.target.state?.opened) {
      return;
    }
    if (IsUnaryFilter(item.operator)) {
      value = null;
    }
    if (currentValue === null && !IsUnaryFilter(item.operator)) {
      value = undefined;
    }
    onChange({
      value,
      operator: item.operator,
      syntheticEvent: event.syntheticEvent,
    });
  };

  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }

    const updatedFilters = filterObject.filters
      .map((item: any) => {
        // Se è il filtro da rimuovere
        if (item.field === fieldName) {
          return null;
        }

        // Se è un gruppo di filtri annidato
        if (item.filters && Array.isArray(item.filters)) {
          const updatedGroup = removeFilterByField(item, fieldName);
          // Mantieni solo se il gruppo ha ancora filtri
          if (updatedGroup.filters.length > 0) {
            return updatedGroup;
          } else {
            return null;
          }
        }
        return item;
      })
      .filter((item: any) => item !== null);

    // Sempre restituire un oggetto con 'logic' e 'filters'
    return {
      logic: filterObject.logic || 'and',
      filters: updatedFilters
    };
  };

  const GridColumnMenuFilterCell = (props: any) => {

    const gridColumnMenuFilterCell = useRef<any>(null);

    const handleResetClick = () => {
      const field = props.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');

    const containsDateTimeField = (filters: any): boolean => {
      if (!Array.isArray(filters)) return false;

      return filters.some((filter: any) => {
        if (filter.field === 'dateTime') {
          return true;
        }
        // Se ha sotto-filtri, ricorsivamente cerca anche lì
        if (Array.isArray(filter.filters)) {
          return containsDateTimeField(filter.filters);
        }
        return false;
      });
    };

    const [tempDateTime, setTempDateTime] = useState<Date | any>(null);

    const handleTempDateTimeChange = (value: Date | null) => {
      setTempDateTime(value);
    };

    const operatorChange = (event: any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };

    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );

      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;

      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };

    useEffect(() => {
      if (!tempDateTime) return;

      // Serve solo a far vedere a Kendo che c'è un valore
      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: new Date(tempDateTime.setMilliseconds(0))
      });
    }, [tempDateTime]);


    useEffect(() => {
      if (props.value && !tempDateTime) {
        setTempDateTime(new Date(props.value));
      }
    }, [props.value]);

    const applyDateTimeFilter = () => {
      if (!tempDateTime) return;

      const cleanDate = new Date(tempDateTime);
      cleanDate.setMilliseconds(0);

      const newFilter = {
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      };

      const existingFilters = dataState?.filter?.filters || [];

      const updatedFilters = existingFilters.filter((f: any) => {
        if (f.filters) {
          return !f.filters.some((inner: any) => inner.field === props.field);
        }
        return f.field !== props.field;
      });

      updatedFilters.push({
        logic: 'or',
        filters: [newFilter],
      });

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilters,
        },
      };

      setDataState(newState);

      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      });

      let filterName: any = []
      updatedFilters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)

      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');

      getDeviceList();
    };

    useEffect(() => {
      const handleDocumentClick = (e: MouseEvent) => {
        const resetButton = (e.target as HTMLElement).closest('button[type="reset"]');
        const applyButton = (e.target as HTMLElement).closest('button.k-button');

        const resetText = resetButton?.textContent?.trim();
        const applyText = applyButton?.textContent?.trim();

        if (resetText === 'Ripulisci' || resetText === 'Clear') {
          handleResetClick();
        } else if (applyText === 'Filtro' || applyText === 'Filter') {
          applyDateTimeFilter();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, [tempDateTime, selectedOperator]);

    return (
      <div
        ref={gridColumnMenuFilterCell}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ zIndex: 10000, position: 'relative' }}
      >
        {renderOperatorEditor()}

        <DatePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

        <div style={{ marginBottom: '10px' }}></div>

        <TimePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'hh:mm:ss a' : 'HH:mm:ss'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

      </div>
    );
  };

  const CustomGridFilterDateTime = (props: any) => {
    const {
      firstFilterProps
    } = props;

    return (
      <GridColumnMenuFilterCell
        {...firstFilterProps}
      />
    );
  };

  const ColumnMenuCheckboxFilter = (props: any) => {

    useEffect(() => {

      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);

      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    //called when the filter is opened
    useEffect(() => {
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    const handleFilterChange = (filterValue: any) => {

      const newFilter: any = filterValue
        ? { ...filterValue }
        : { logic: 'and', filters: [] };

      const newSkip = dataState.skip ?? 0;
      const newTake = dataState.take ?? 10;

      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

      if (dataState) {
        const { sort } = dataState;

        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter,
          sort: sort,
          skip: 0,
          take: newTake
        }));
      }

      // Salva lo stato aggiornato completo nel localStorage
      const updatedState = {
        dataState: {
          ...dataState,
          filter: newFilter,
          skip: newSkip,
          take: newTake
        },
        columns
      };
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));

    };

    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={devicesOn}
          expanded={true}
          searchBoxFilterOperator={'contains'}
          onFilterChange={handleFilterChange}
        />
      </div>
    );
  }

  const CustomInfoCheckboxFilter = (props: any) => {

    useEffect(() => {

      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      //setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      setFilterName((prev: any) =>
        Array.isArray(prev) ? prev.filter((item: any) => item !== field) : []
      );
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
      localStorage.setItem('filterNameSynoptic', JSON.stringify([]));
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    let currDevice: any = []
    devicesOn.forEach((device: any) => {
      if (device.customInfoDictionary != null && device.customInfoDictionary != '') {
        currDevice.push(device)
      }
    })

    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={currDevice}
          expanded={true}
          searchBoxFilterOperator={'contains'}
        />
      </div>
    );
  }

  const ColumnMenuNumericFilter = (props: any) => {

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    useEffect(() => {
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);

      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });

      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    if (!props || !props.column) {
      return null;
    }

    const customFilterOperators: any = {
      numeric: [
        { text: 'grid.filterEqOperator', operator: 'eq' },
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterGteOperator', operator: 'gte' },
        { text: 'grid.filterGtOperator', operator: 'gt' },
        { text: 'grid.filterLteOperator', operator: 'lte' },
        { text: 'grid.filterLtOperator', operator: 'lt' }

      ]
    };


    const handleFilterChange = (filterValue: any) => {

      const newFilter: any = filterValue
        ? { ...filterValue }
        : { logic: 'and', filters: [] };

      const newSkip = dataState.skip ?? 0;
      const newTake = dataState.take ?? 10;

      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

      if (dataState) {
        const { sort } = dataState;

        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter,
          sort: sort,
          skip: 0,
          take: newTake
        }));
      }

      // Salva lo stato aggiornato completo nel localStorage
      const updatedState = {
        dataState: {
          ...dataState,
          filter: newFilter,
          skip: newSkip,
          take: newTake
        },
        columns
      };
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));

    };


    return (
      <GridColumnMenuFilter
        {...props}
        data={devicesOn}
        expanded={true}
        hideSecondFilter={true}
        filterOperators={customFilterOperators}
        onFilterChange={handleFilterChange}
      />
    );
  };

  const StatusCellWithColorComponent = (props: GridCellProps) => {

    const statusDevice: any = props.dataItem.status;
    const statusDotStyle = {
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      display: 'block',
      margin: '0 auto',
      boxShadow: '0 0 0 2px #ffffff, 0 0 0 3px rgba(7, 63, 72, 0.24)'
    };

    if (statusDevice == 0) {
      return (
        <td>
          <span className='statusRed synoptic-status-dot' style={statusDotStyle}></span>
        </td>
      );
    } else {

      if (statusDevice != 1) {
        return (
          <td>
            <span className='statusRed synoptic-status-dot' style={statusDotStyle}></span>
          </td>
        );
      }
      else {
        return (
          <td>
            <span className='statusGreen synoptic-status-dot' style={statusDotStyle}></span>
          </td>
        );
      }
    }
  };

  const InChargeCellWithColorComponent = (props: GridCellProps) => {
    const inCharge = props.dataItem.inCharge;
    const inChargeDotStyle = {
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      display: 'block',
      margin: '0 auto',
      boxShadow: '0 0 0 2px #ffffff, 0 0 0 3px rgba(7, 63, 72, 0.24)'
    };

    if (inCharge == null) {
      return (
        <td
          style={{ textAlign: 'center' }}
          title={localizationService.toLanguageString('synoptic_devices.warning_battery', 'NOT_FOUND')}
        >
          <SvgIcon icon={warningTriangleIcon} size="medium" />
        </td>
      );
    }
    else if (inCharge == 0) {
      return (
        <td>
          <span
            className='inChargeRed synoptic-status-dot'
            style={inChargeDotStyle}
            title={localizationService.toLanguageString('synoptic_devices.inChargeRed', 'NOT_FOUND')}
          ></span>
        </td>
      );
    }
    else {
      return (
        <td>
          <span
            className='inChargeGreen synoptic-status-dot'
            style={inChargeDotStyle}
            title={localizationService.toLanguageString('synoptic_devices.inChargeGreen', 'NOT_FOUND')}
          ></span>
        </td>
      );
    }
  };

  const GroupCellWithColorComponent = (props: any) => {
    const groupName = props.dataItem.groupName
    const groupColor = props.dataItem.groupColor
    return (
      <td>
        <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          <div className='groupColor' style={{ backgroundColor: groupColor }} />
          <span>{groupName}</span>
        </div>
      </td>
    )
  };

  const JobCellWithColorComponent = (props: any) => {
    const jobName = props.dataItem.jobName
    const jobColor = props.dataItem.jobColor
    return (
      <td>
        <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          <div className='jobColor' style={{ backgroundColor: jobColor }} />
          <span style={{ minWidth: '100px' }}>{jobName}</span>
        </div>
      </td>
    )
  };

  const TemperatureCellWithBackGroundComponent = (props: any) => {
    const temperatureValue = props?.dataItem?.temperature;
    if (temperatureValue != null) {
      return (
        <td style={{ textAlign: 'center' }}>
          <span className='k-table-row k-master-row temperature'>{temperatureValue} °C</span>
        </td>
      )
    } else {
      return (
        <td style={{ textAlign: 'center' }}><span className='k-table-row k-master-row temperature' /></td>
      )
    }
  };

  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.dateTime);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const dateTimeValue: any = formatDateFromIsoToString(date);
    if (dateTimeValue != null && !dateTimeValue?.includes('1970')) {
      return (
        <td>
          <span className='k-table-row k-master-row date-time'>
            {dateTimeValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row date-time' /></td>
      )
    }
  };

  const LastPositionDateCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.lastPositionDate);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const lastPositionDateValue: any = formatDateFromIsoToString(date);
    if (lastPositionDateValue != null && !lastPositionDateValue.includes('1970') && !lastPositionDateValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row last-position'>
            {lastPositionDateValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row last-position' /></td>
      )
    }
  };

  const LocalLastPositionDateCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.localLastPositionDate);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const localLastPositionDateValue: any = formatDateFromIsoToString(props?.dataItem?.localLastPositionDate);
    if (localLastPositionDateValue != null && !localLastPositionDateValue.includes('1970') && !localLastPositionDateValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row local-last-position'>
            {localLastPositionDateValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row local-last-position' /></td>
      )
    }
  };

  const LocalTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.localTime);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const localTimeValue: any = formatDateFromIsoToString(props?.dataItem?.localTime);
    if (localTimeValue != null && !localTimeValue.includes('1970') && !localTimeValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row local-time'>
            {localTimeValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row local-time' /></td>
      )
    }
  };

  const BatteryCellWithBackGroundComponent = (props: any) => {
    const batteryValue = props.dataItem.battery;
    //const batteryValueWithoutPercentage = batteryValue.trim().replace('%', '');

    if (!batteryValue) {
      return (
        <td style={{ textAlign: 'center' }}>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15))' }}>
            <title>?</title>
            <g transform="rotate(90 32 32)">
              <g fill="black">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="black">
                <text
                  x="35"
                  y="45"
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="bold"
                  fill="black"
                  transform="rotate(-90 32 38)"
                >
                  ?
                </text>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue < 25) {
      return (
        <td style={{ textAlign: 'center' }}>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15))' }}>
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="#d62323">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue >= 25 && batteryValue <= 50) {
      return (
        <td style={{ textAlign: 'center' }}>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15))' }}>
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="#d99a12">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else if (batteryValue > 50 && batteryValue <= 75) {
      return (
        <td style={{ textAlign: 'center' }}>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15))' }}>
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="#5ea832">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else {
      return (
        <td style={{ textAlign: 'center' }}>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15))' }}>
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="#1e8b3a">
                <path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
  };

  const AlarmCellWithBackGroundComponent = (props: any) => {
    const alarmValue = props.dataItem.hasAlarm;

    const navigate = useNavigate()

    const showAlarm = () => {
      let deviceMac = props?.dataItem?.mac;
      if (deviceMac != null) {
        localStorage.setItem("deviceMacFromSynopticInAlarm", deviceMac);
      }
      navigate("/home/alarms")
    }

    if (alarmValue) {
      const alarmTitle = localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND');
      return (
        <td className="k-command-cell" style={{ textAlign: 'center' }}>
          <button
            type='button'
            title={alarmTitle}
            aria-label={alarmTitle}
            style={{
              color: '#0f6671',
              border: 'none',
              background: '#e8f5f7',
              outline: 'none',
              borderRadius: '999px',
              padding: '6px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(11, 74, 84, 0.2)'
            }}
            className='synoptic-cell-action'
            onClick={showAlarm}
          >
            <SvgIcon icon={alarm} className={'alarmButtonSynoptic'} size="medium" />
          </button>
        </td>
      )
    }
    else {
      return (
        <td>
          <span></span>
        </td>
      )
    }
  };

  const defaultColumns: any = [
    { field: 'status', title: localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND'), editable: false, filterable: false, show: true, exportable: false },
    { field: 'mac', title: localizationService.toLanguageString('synoptic_devices.mac', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, exportable: false },
    { field: 'serialNumber', title: localizationService.toLanguageString('synoptic_devices.serialNumber', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'name', title: localizationService.toLanguageString('synoptic_devices.name', 'NOT_FOUND'), editor: "text", columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: true, show: true, hidden: false, exportable: false },
    { field: 'groupName', title: localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND'), columnMenu: GroupCellWithColorComponent, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'jobName', title: localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND'), columnMenu: JobCellWithColorComponent, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'zoneNameListJoined', title: localizationService.toLanguageString('synoptic_devices.zoneNameListJoined', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'deviceTypeName', title: localizationService.toLanguageString('synoptic_devices.deviceTypeName', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, hidden: false, exportable: false },
    { field: 'temperature', title: localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND'), filter: "numeric", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'localTime', title: localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: false, hidden: false, exportable: false },
    { field: 'dateTime', title: localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'lastPositionDate', title: localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'localLastPositionDate', title: localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: false, hidden: false, exportable: false },
    { field: 'customInfo', title: localizationService.toLanguageString('synoptic_devices.customInfo', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'battery', title: localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND'), cell: BatteryCellWithBackGroundComponent, filterable: true, filter: "numeric", editable: false, show: true, exportable: false },
    { field: 'inCharge', title: localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND'), filterable: false, editable: false, show: true, exportable: false },
    { field: 'hasAlarm', title: localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND'), cell: AlarmCellWithBackGroundComponent, filterable: false, editable: false, show: true, exportable: false },
    { field: 'technology', title: localizationService.toLanguageString('synoptic_devices.technology', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: false, exportable: true },
    //...customInfoDictionaryColumns
  ];

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: 10,
    data: devices,
    total: devices?.length,
    filter: { logic: 'and', filters: [] },
    //sort: [{ field: 'mac', dir: 'desc' }]
  });

  const filteredGridFields = useMemo(() => {
    const fields = new Set<string>();

    const collectFields = (filter: any): void => {
      if (!filter || !Array.isArray(filter.filters)) {
        return;
      }

      filter.filters.forEach((entry: any) => {
        if (entry && Array.isArray(entry.filters)) {
          collectFields(entry);
          return;
        }

        const field = typeof entry?.field === 'string' ? entry.field.trim() : '';
        if (field.length > 0) {
          fields.add(field);
        }
      });
    };

    collectFields(dataState?.filter);
    return Array.from(fields);
  }, [dataState?.filter]);

  const hasActiveGridFilters = filteredGridFields.length > 0;

  const [originalNames, setOriginalNames] = useState<Record<string, string>>({});

  //used when update device name
  const update = (dataItem: any) => {

    dataItem.inEdit = false;

    const devicesOnCopy = [...devicesOn];

    let index: any = devicesOn.findIndex((record: any) => record.mac === dataItem.mac);
    devicesOnCopy[index] = dataItem;

    var currentDate = new Date()
    var currentTime = formatDateFromIsoToString(currentDate)
    editFieldName(dataItem.mac, dataItem.name, dataItem.groupId, dataItem.jobId, currentTime, dataItem.serialNumber);
    setDevicesOn(devicesOnCopy);

    // Rimuovi il filtro "name" se presente dopo il rename
    const currentFilterName = safeParseJSON<string[]>(localStorage.getItem("filterNameSynoptic"), []);
    const updatedFilterName = currentFilterName.filter((item: string) => (
      item !== 'name' && item !== 'nameEdit'
    ));
    localStorage.setItem("filterNameSynoptic", JSON.stringify(updatedFilterName));
    setFilterName(updatedFilterName);

    // Rimuovi anche il filtro "name" dal dataState
    if (dataState?.filter?.filters) {
      const updatedFilter = removeFilterByField(dataState.filter, 'name');
      const newState = {
        ...dataState,
        filter: updatedFilter
      };
      setDataState(newState);
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
    }

    setOriginalNames(prev => ({ ...prev, [dataItem.mac]: dataItem.name }));
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const cancel = (dataItem: any) => {
    setDevicesOn((prev: any) =>
      prev.map((d: any) =>
        d.mac === dataItem.mac
          ? { ...d, name: originalNames[dataItem.mac], inEdit: false } // ripristina vecchio nome e chiude edit
          : d
      )
    );
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const enterEdit = (dataItem: any) => {
    setEditMode(true);

    const newDevice = devicesOn.map((item: any) =>
      item.mac === dataItem.mac
        ? { ...item, inEdit: true }
        : item
    );

    // Legge da localStorage e aggiorna in modo sicuro
    const currentFilter = safeParseJSON<string[]>(localStorage.getItem("filterNameSynoptic"), []);

    // Aggiunge 'nameEdit' solo se non esiste già
    const updatedFilter = Array.from(new Set([...currentFilter, 'nameEdit']));

    localStorage.setItem("filterNameSynoptic", JSON.stringify(updatedFilter));
    setFilterName(updatedFilter);

    setDevicesOn(newDevice);
    setOriginalNames(prev => ({ ...prev, [dataItem.mac]: dataItem.name }));
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: true }));
  };

  const itemChange = (event: GridItemChangeEvent) => {
    setDevicesOn((prevDevices: any) =>
      prevDevices.map((device: any) =>
        device.mac === event.dataItem.mac
          ? { ...device, [event.field || ""]: event.value }
          : device
      )
    );
  };

  const commandCellProps = {
    edit: enterEdit,
    update: update,
    cancel: cancel,
    editField: editField
  };

  const saveStateToLocalStorage = () => {
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {

    const storedFilter = localStorage.getItem(`gridSynopticState: ${userEmail}`);
    if (storedFilter) {
      const parsedStoredFilter = safeParseJSON<any>(storedFilter, null);
      if (!parsedStoredFilter?.dataState || !parsedStoredFilter?.columns) {
        setColumns(defaultColumns)
        return null;
      }

      const { dataState: savedDataState, columns: savedColumns } = parsedStoredFilter;

      const localizedColumns = savedColumns.map((column: any) => ({
        ...column,
        title: localizationService.toLanguageString(`synoptic_devices.${column.field}`, 'NOT_FOUND')
      }));

      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;

            if (['dateTime', 'localTime', 'localLastPositionDate', 'lastPositionDate'].includes(field)) {
              value = new Date(value);
            }

            return {
              ...filter,
              value,
            };
          }
        };

        const updatedFilter = updateFilterValues(savedDataState.filter);

        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };

        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }

      if (visibleCountSynoptic === 0 && !firstLoad) {

        const storedState = safeParseJSON<any>(localStorage.getItem(`gridSynopticState: ${userEmail}`), {});

        const updatedState = {
          ...storedState,
          dataState: {
            ...storedState.dataState,
            skip: 0
          }
        };

        localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
      }

      setFirstLoad(false)


      // Column management remains unchanged
      let currentColumn: any = localizedColumns?.map((column: any) => {
        return column;
      });

      setColumns(currentColumn)

      return storedFilter ? storedFilter : currentColumn;
    } else {
      setColumns(defaultColumns)
    }
  };

  //toolbar buttons functions
  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);

    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const handleColumnReorder = (event: any) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };

  const onDataStateChange = (event: GridDataStateChangeEvent) => {

    const newFilter: any = event.dataState.filter
      ? { ...event.dataState.filter }
      : { logic: 'and', filters: [] };

    let filterName: any = []
    newFilter?.filters?.forEach((filter: any) => {
      filter?.filters?.forEach((filterInfo: any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));
    }
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const CellRender = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
      >
        {props.children}
      </td>
    );
  }

  const sanitizeFilter = (filter: any) => {
    if (filter && typeof filter === 'object' && Array.isArray(filter.filters)) {
      const flatFilters = filter.filters.flatMap((f: any) =>
        Array.isArray(f.filters) ? f.filters : [f]
      );

      return {
        logic: filter.logic || 'and',
        filters: flatFilters
      };
    }
    return { logic: 'and', filters: [] };
  };

  const safeFilter = sanitizeFilter(dataState?.filter);

  const [processedData, setProcessedData] = useState<any>({ data: [], total: 0 });

  useEffect(() => {
    //if (!devicesOn || devicesOn.length === 0) return;

    const result = kendoProcess(devicesOn, {
      filter: dataState?.filter ?? safeFilter,
      sort: dataState?.sort,
      skip: dataState?.skip,
      take: dataState?.take,
    });

    setProcessedData(result);

    if (result.data.length === 0 && result.total > 0 && dataState.skip > 0 && !firstLoad) {
      setDataState((prev: any) => ({ ...prev, skip: 0 }));
    }
  }, [devicesOn, dataState?.filter, dataState?.sort, dataState?.skip, dataState?.take]);



  const onPageChange = (event: any) => {

    const allItems: any = localizationService.toLanguageString('synoptic_devices.all', 'NOT_FOUND')
    const targetEvent = event.targetEvent;
    const newSkip = event?.page?.skip;
    const newTake = event?.targetEvent?.value === allItems ? devicesOn?.length : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    const newDataState = {
      ...dataState,
      skip: newSkip,
      take: newTake
    };

    setDataState(newDataState);

    // Calcola manualmente la porzione visibile
    const processedPage = kendoProcess(devicesOn, newDataState);
    const visibleNow = processedPage.data.length;
    setVisibleCount(visibleNow);
    localStorage.setItem("visibleCountSynoptic", JSON.stringify(visibleNow));
    // Salva lo stato aggiornato completo nel localStorage
    const updatedState = {
      dataState: {
        ...dataState,
        skip: newSkip,
        take: newTake
      },
      columns
    };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
  };

  const _excelExport = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_excelExport.current !== null) {
      _excelExport?.current?.save(devicesOn);
    }
  };

  const _pdfExport = useRef<GridPDFExport | null>(null);
  const pdfExport = () => {
    if (_pdfExport.current !== null) {
      _pdfExport?.current?.save(devicesOn);
    }
  };

  const visibleColumns = useMemo(() => columns?.filter((col: any) => col?.show), [columns]);

  const handleExportCsv = async () => {

    const filteredData: any = devicesOn.map((item: any) => {
      const filteredItem: any = {};
      visibleColumns.forEach((col: any) => {
        filteredItem[col.field] = item[col.field];
      });
      return filteredItem;
    });

    try {
      const csv = await json2csv(filteredData);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "data.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };


  const onToggleLive = (e: any) => {
    const toggleValue = e?.value ?? e;
    const nextLiveStatus = typeof toggleValue === 'string' ? toggleValue === 'true' : Boolean(toggleValue);

    localStorage.setItem('liveStatus', JSON.stringify(nextLiveStatus))
    setLiveStatus(nextLiveStatus)

    const onlyBound = getOnlyBoundFlag();

    if (nextLiveStatus) {
      const minutesBefore = statusDevice

      let liveDevicesToShowInGrid: any = []
      let dateBeforeMinutes: any = ''

      //devicesList[0].lastPositionDate = '12/04/2024 8:00:00 AM' // for test
      devicesOn.forEach((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore)
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            //hour12: true // Force 12-hour format with AM/PM
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) === 1) {
            liveDevicesToShowInGrid.push(device)
          }
        }
      })
      setDevicesOn(liveDevicesToShowInGrid)

      if (onlyBound) {
        getDeviceList(onlyBound)
      } else {
        getDeviceList()
      }
    } else {
      if (onlyBound) {
        getDeviceList(onlyBound)
      } else {
        getDeviceList()
      }
    }

    const storedState = safeParseJSON<any>(localStorage.getItem(`gridSynopticState: ${userEmail}`), {});

    if (visibleCountSynoptic === 0 && !firstLoad) {

      const updatedState = {
        ...storedState,
        dataState: {
          ...storedState.dataState,
          skip: 0
        }
      };

      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
    }

  }

  const getDateRange = (minutes: number) => {
    // Create a Date object from the input string
    const currentDate = new Date();

    // 2. Calculate milliseconds to subtract:
    const millisecondsToSubtract = minutes * 60 * 1000; // minutes * seconds * milliseconds

    // 3. Subtract the milliseconds:
    currentDate.setTime(currentDate?.getTime() - millisecondsToSubtract);

    const localTimeString = currentDate.toLocaleString(languageId, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      //hour12: true // Force 12-hour format with AM/PM
    });

    return localTimeString
  }

  const convertToDate = (dateString: string, isItalian: boolean = false): Date | null => {
    if (!dateString) return null;

    try {
      const cleanStr = dateString.replace(/,/g, '').trim();
      const parts = cleanStr.split(' ');

      let day: number, month: number, year: number;
      let hours: number = 0, minutes: number = 0, seconds: number = 0;

      if (isItalian) {
        // Formato italiano: "dd/MM/yyyy HH:mm:ss"
        const [datePart, timePart] = parts;
        if (!datePart || !timePart) return null;

        [day, month, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);
      } else {
        // Formato inglese: "MM/dd/yyyy hh:mm:ss AM|PM"
        if (parts.length < 3) return null;

        const [datePart, timePart, ampm] = parts;
        [month, day, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }

      if ([day, month, year, hours, minutes, seconds].some(n => isNaN(n))) return null;

      const date = new Date(year, month - 1, day, hours, minutes, seconds);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const compareDateTimes = (date1Str: any, date2Str: any) => {

    let currLanguage = languageId
    if (currLanguage == 'it-IT') {
      currLanguage = true
    } else {
      currLanguage = false
    }
    // Parse the dates into Date objects    
    const date1: any = convertToDate(date1Str, currLanguage);
    const date2: any = convertToDate(date2Str, currLanguage);

    // Compare the milliseconds
    if (date1?.getTime() < date2?.getTime()) {
      return -1; // date1 is earlier
    } else if (date1?.getTime() > date2?.getTime()) {
      return 1; // date1 is later
    } else {
      return 0; // dates are equal
    }
  }

  const onToggleOnlyBound = (e: any) => {
    const toggleValue = e?.value ?? e;
    const nextOnlyBound = typeof toggleValue === 'string' ? toggleValue === 'true' : Boolean(toggleValue);

    localStorage.setItem('onlyBound', JSON.stringify(nextOnlyBound))
    setOnlyBound(nextOnlyBound)
    if (liveStatus) {
      onToggleLive(liveStatus)
    } else {
      getDeviceList(nextOnlyBound)
    }
    const newSkip = 0;
    const newTake = 10;
    setDataState((prevState: any) => ({
      ...prevState,
      skip: newSkip,
      take: dataState.take ?? newTake
    }));
    const updatedState = {
      dataState: {
        ...dataState,
        skip: newSkip,
        take: dataState.take ?? newTake
      },
      columns
    };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
    //getDeviceList(nextOnlyBound)
  }

  const onToggleMqttConnection = (e: any) => {
    const toggleValue = e?.value ?? e;
    const nextMqttConnectionEnabled = typeof toggleValue === 'string' ? toggleValue === 'true' : Boolean(toggleValue);

    setMqttConnectionEnabledState(nextMqttConnectionEnabled);
    setGlobalMqttConnectionEnabled(nextMqttConnectionEnabled, SERVER_URL);
  }

  const IconHeaderCell = (props: any) => {
    const field = typeof props?.field === 'string' ? props.field : '';
    const isFilteredColumn = field !== '' && filteredGridFields.includes(field);
    const mergedThProps = {
      ...(props?.thProps ?? {}),
      className: `${props?.thProps?.className ?? ''}${isFilteredColumn ? ' synoptic-filtered-header' : ''}`.trim()
    };

    return (
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...mergedThProps}>
        {props.children}
      </HeaderThElement>
    );
  };

  const synopticSurfaceStyle = {
    position: 'relative' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    width: 'calc(100% - 20px)',
    height: 'calc(100% - 20px)',
    minHeight: 0,
    overflow: 'hidden' as const,
    boxSizing: 'border-box' as const,
    margin: '10px',
    borderRadius: '12px',
    background: 'linear-gradient(180deg, #f5fbfc 0%, #eaf5f7 100%)',
    border: '1px solid #bdd8dd',
    boxShadow: '0 14px 30px rgba(9, 64, 72, 0.2)'
  };

  const toolbarStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    height: '75px',
    minHeight: '75px',
    maxHeight: '75px',
    flex: '0 0 75px',
    boxSizing: 'border-box' as const,
    alignItems: 'center',
    borderRadius: '10px 10px 0px 0px',
    background: 'linear-gradient(180deg, #d8edf1 0%, #cfe8ed 100%)',
    border: 'none',
    borderBottom: '1px solid #9ec8cf'
  };

  const toolbarToggleGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap' as const,
    padding: '5px 10px',
    borderRadius: '999px',
    backgroundColor: '#f4fbfc',
    border: '1px solid #a8ced4',
    color: '#164f58',
    fontWeight: 600
  };

  const toolbarActionsStyle = {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    padding: '4px',
    borderRadius: '10px',
    backgroundColor: '#ecf7f9'
  };

  const toolbarButtonStyle = {
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(10, 74, 84, 0.18)'
  };

  const toolbarLabelStyle = {
    color: '#154e57',
    fontWeight: 700,
    letterSpacing: '0.01em',
    margin: '0px'
  };

  const onRefreshGrid = () => {
    const nextOnlyBound = getOnlyBoundFlag();
    setOnlyBound(nextOnlyBound);
    getDeviceList(nextOnlyBound);
  };

  const synopticMotionCss = `
    @keyframes synopticFadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes synopticPopIn {
      from { opacity: 0; transform: scale(0.98); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes synopticPulseSoft {
      0% { box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(7, 63, 72, 0.18); }
      50% { box-shadow: 0 0 0 2px #ffffff, 0 0 0 7px rgba(7, 63, 72, 0.28); }
      100% { box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(7, 63, 72, 0.18); }
    }

    .synoptic-animated-surface {
      animation: synopticFadeUp 260ms ease-out both;
    }

    .drawer-layout-shell:has(.synoptic-animated-surface) {
      height: calc(100vh - 132px);
      min-height: 0;
    }

    .drawer-content-shell:has(.synoptic-animated-surface) {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .drawer-content-shell:has(.synoptic-animated-surface) > .synoptic-animated-surface {
      flex: 0 0 auto;
      min-height: 0;
    }

    .synoptic-grid-colorized {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
      box-sizing: border-box;
    }

    .synoptic-grid-colorized .k-grid-toolbar,
    .synoptic-grid-colorized .k-grid-header,
    .synoptic-grid-colorized .k-grid-pager {
      flex: 0 0 auto;
    }

    .synoptic-grid-colorized .k-grid-container {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
    }

    .synoptic-grid-colorized .k-grid-aria-root {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }

    .synoptic-grid-colorized .k-grid-content {
      flex: 1 1 auto;
      min-height: 0;
      overflow-x: auto;
      overflow-y: auto;
    }

    .synoptic-animated-toolbar {
      animation: synopticPopIn 320ms ease-out both;
    }

    .synoptic-animated-toolbar-secondary {
      animation-delay: 80ms;
    }

    .synoptic-toggle-pill {
      transition: transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease;
    }

    .synoptic-toggle-pill:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(10, 74, 84, 0.18);
      border-color: #7fb5bf;
    }

    .synoptic-toolbar-actions {
      transition: box-shadow 200ms ease;
    }

    .synoptic-toolbar-actions:hover {
      box-shadow: inset 0 0 0 1px rgba(13, 90, 102, 0.16);
    }

    .synoptic-action-btn {
      transition: transform 170ms ease, box-shadow 170ms ease, filter 170ms ease;
    }

    .synoptic-action-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(8, 65, 73, 0.24);
      filter: saturate(1.05);
    }

    .synoptic-cell-action {
      transition: transform 170ms ease, box-shadow 170ms ease, background-color 170ms ease;
    }

    .synoptic-cell-action:hover {
      transform: scale(1.3);
      box-shadow: 0 4px 12px rgba(11, 74, 84, 0.26);
      background-color: #d9eef2 !important;
    }

    .synoptic-status-dot {
      animation: synopticPulseSoft 1.8s ease-in-out infinite;
    }

    .synoptic-grid-colorized .k-table-tbody > tr > td {
      background: linear-gradient(180deg, rgba(250, 254, 255, 0.98) 0%, rgba(240, 250, 252, 0.98) 100%);
      border-bottom: 1px solid #c5dee3;
      border-right: 1px solid #d6e9ed;
      color: #114f58;
      font-size: 13px;
      padding: 0.38rem 0.5rem;
      line-height: 1.2;
      vertical-align: middle;
      transition: background-color 170ms ease, box-shadow 170ms ease;
    }

    .synoptic-grid-colorized .k-table-tbody > tr > td .k-table-row,
    .synoptic-grid-colorized .k-table-tbody > tr > td .k-master-row,
    .synoptic-grid-colorized .k-table-tbody > tr > td .groupColor + span,
    .synoptic-grid-colorized .k-table-tbody > tr > td .jobColor + span {
      color: inherit;
      font-size: 13px;
      font-weight: inherit;
    }

    .synoptic-grid-colorized .battery-svg {
      width: 30px;
      height: 30px;
    }

    .synoptic-grid-colorized .k-table-tbody > tr:nth-child(odd) > td {
      background: linear-gradient(180deg, rgba(251, 255, 255, 0.99) 0%, rgba(242, 251, 253, 0.99) 100%);
    }

    .synoptic-grid-colorized .k-grid-toolbar {
      border-radius: 0 !important;
      border-left: none !important;
      border-right: none !important;
      margin: 0;
    }

    .synoptic-grid-colorized .k-table-tbody > tr:nth-child(even) > td {
      background: linear-gradient(180deg, rgba(234, 247, 250, 0.98) 0%, rgba(223, 241, 245, 0.98) 100%);
    }

    .synoptic-grid-colorized .k-table-tbody > tr:hover > td {
      background: linear-gradient(180deg, #e4f4f7 0%, #d7edf1 100%) !important;
      box-shadow: inset 0 0 0 1px rgba(10, 85, 97, 0.14);
    }

    .synoptic-grid-colorized .k-table-tbody > tr.k-state-selected > td,
    .synoptic-grid-colorized .k-table-tbody > tr.k-selected > td {
      background: linear-gradient(180deg, #d8edf2 0%, #cde6ed 100%) !important;
      color: #0f4650;
    }

    .synoptic-grid-colorized .k-grid-header th {
      background: linear-gradient(180deg, #d4eaf0 0%, #c7e1e8 100%);
      color: #0d4a54;
      font-size: 13px;
      border-bottom: 1px solid #9ec5cc;
      border-right: 1px solid #b7d6dc;
      padding: 0.62rem 0.62rem;
    }

    .synoptic-grid-colorized .k-grid-header .k-sort-icon,
    .synoptic-grid-colorized .k-grid-header .k-svg-icon {
      color: #0f6671;
    }

    .synoptic-grid-colorized .synoptic-filtered-header .k-grid-header-menu.k-grid-column-menu {
      background: #D4EBEF !important;
      border-radius: 4px;
    }

    .synoptic-grid-colorized .k-grid-norecords td {
      padding: 0 !important;
      border-bottom: 1px solid #c5dee3;
    }

    .synoptic-grid-colorized .synoptic-no-records {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      letter-spacing: 0.01em;
      color: #0f6671;
      background: linear-gradient(180deg, rgba(246, 252, 253, 0.95) 0%, rgba(235, 246, 248, 0.95) 100%);
    }

    @media (prefers-reduced-motion: reduce) {
      .synoptic-animated-surface,
      .synoptic-animated-toolbar,
      .synoptic-animated-toolbar-secondary,
      .synoptic-toggle-pill,
      .synoptic-toolbar-actions,
      .synoptic-action-btn,
      .synoptic-cell-action,
      .synoptic-status-dot {
        animation: none !important;
        transition: none !important;
      }
    }

    @media (max-width: 1550px) {
      .synoptic-animated-surface {
        animation: synopticFadeUp 260ms ease-out both;
      }
    }

  `;

  const noDevicesLabel = localizationService.toLanguageString('synoptic_devices.no_device_found', 'Nessun dispositivo presente');
  const noRecordsLabel = error || noDevicesLabel;

  const isSynopticGridEmpty = !loading && (processedData?.data?.length ?? 0) === 0;

  return (
    <div
      style={synopticSurfaceStyle}
      className={`synoptic-animated-surface${isSynopticGridEmpty ? ' synoptic-compact-surface' : ''}`}
    >
      <style>{synopticMotionCss}</style>
      {loading && loadingPanel}

      <ExcelExport data={devicesOn} ref={_excelExport}>
        {columns
          .map((col: any) => (
            <ExcelExportColumn
              key={col.field}
              field={col.field}
              title={localizationService.toLanguageString(`synoptic_devices.${col.field}`, col.title)}
              cellOptions={{
                format:
                  col.field.includes('dateTime') || col.field.includes('localTime') || col.field.includes('localLastPositionDate') || col.field.includes('lastPositionDate')
                    ? languageId === 'en-US'
                      ? 'MM/dd/yyyy hh:mm:ss AM/PM'
                      : 'dd/MM/yyyy HH:mm:ss'
                    : undefined
              }}
            />
          ))}
      </ExcelExport>

      <Grid
        className={`synoptic-grid-colorized${hasActiveGridFilters ? ' grid-filter-active' : ''}`}
        data={processedData}
        dataItemKey={"mac"}
        //style={{ height: '70vh' }}
        cells={{ data: CellRender }}
        sortable={true}
        take={dataState?.take ?? 10}
        skip={dataState?.skip ?? 0}
        pageable={{ type: 'input', buttonCount: 5, pageSizes: [10, 20, 50, 100, localizationService.toLanguageString('synoptic_devices.all', 'NOT_FOUND')], pageSizeValue: pageSizeValue, info: true }}
        reorderable={false}
        sort={dataState?.sort}
        filter={dataState?.filter}
        onColumnResize={onColumnResize}
        onDataStateChange={onDataStateChange}
        onPageChange={onPageChange}
        onItemChange={itemChange}
        edit={edit}
        editable={true}
        scrollable={'none'}
      >

        <GridNoRecords>
          <div className='synoptic-no-records'>{noRecordsLabel}</div>
        </GridNoRecords>

        <GridToolbar style={toolbarStyle} className='synoptic-animated-toolbar'>
          <div style={{ minWidth: 'fit-content' }}>
                      <h3 className="page-title">{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
          </div>
          <div style={toolbarToggleGroupStyle} className='synoptic-toggle-pill'>
            <Switch
              onChange={onToggleLive}
              checked={liveStatus}
            />
            <label style={toolbarLabelStyle}>{localizationService.toLanguageString('synoptic_devices.live', 'NOT_FOUND')}</label>
          </div>
          <div style={toolbarToggleGroupStyle} className='synoptic-toggle-pill'>
            <Switch
              onChange={onToggleOnlyBound}
              checked={onlyBound}
            />
            <label style={toolbarLabelStyle}>{localizationService.toLanguageString('synoptic_devices.only_binded', 'NOT_FOUND')}</label>
          </div>
          <div style={toolbarToggleGroupStyle} className='synoptic-toggle-pill'>
            <Switch
              onChange={onToggleMqttConnection}
              checked={mqttConnectionEnabled}
            />
            <label style={toolbarLabelStyle}>{localizationService.toLanguageString('synoptic_devices.mqtt_connection', 'NOT_FOUND')}</label>
          </div>

          <div style={toolbarActionsStyle} className='synoptic-toolbar-actions'>
            <div>
              <Button
                type="button"
                rounded="full"
                fillMode="solid"
                themeColor="primary"
                size="small"
                svgIcon={arrowRotateCwIcon}
                title={localizationService.toLanguageString('synoptic_devices.refresh', 'Refresh grid')}
                aria-label={localizationService.toLanguageString('synoptic_devices.refresh', 'Refresh grid')}
                onClick={onRefreshGrid}
                style={toolbarButtonStyle}
                className='synoptic-action-btn'
              />
            </div>
            <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
            <div>
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                svgIcon={fileCsvIcon}
                onClick={handleExportCsv}
                style={toolbarButtonStyle}
                className='synoptic-action-btn'
              />
            </div>
            <div>
              <Button
                onClick={pdfExport}
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                svgIcon={filePdfIcon}
                style={toolbarButtonStyle}
                className='synoptic-action-btn'
              />
            </div>
            <div>
              <Button
                title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                themeColor={"primary"}
                size="small"
                svgIcon={fileExcelIcon}
                onClick={excelExport}
                style={toolbarButtonStyle}
                className='synoptic-action-btn'
              />
            </div>
          </div>
        </GridToolbar>
        {columns[2]?.show &&
          <Column
            cells={{ data: (props: any) => <EditingCell  {...props} {...commandCellProps} /> }}
            filterable={false}
            sortable={false}
          />
        }
        <Column
          cells={{ data: LinkMapCell }}
          editable={false}
          filterable={false}
          sortable={false}
        />
        <Column
          cells={{ data: LinkDeviceManagement }}
          editable={false}
          filterable={false}
          sortable={false}
        />
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column?.show && (
                column.field == 'groupName' ?
                  <Column
                    key={`synoptic-col-${column.field ?? index}`}
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND')}
                    cells={{
                      data: (props) => <GroupCellWithColorComponent  {...props} color={column?.groupColor} />,
                      headerCell: IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    columnMenu={ColumnMenuCheckboxFilter}
                    sortable={true}
                  />
                  :
                  column.field == 'jobName' ?
                    <Column
                      key={`synoptic-col-${column.field ?? index}`}
                      field={column.field}
                      title={localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND')}
                      cells={{
                        data: (props) => <JobCellWithColorComponent  {...props} color={column?.jobColor} />,
                        headerCell: IconHeaderCell
                      }}
                      editable={false}
                      filterable={true}
                      columnMenu={ColumnMenuCheckboxFilter}
                      sortable={true}
                    />
                    :
                    column.field == 'temperature' ?
                      <Column
                        key={`synoptic-col-${column.field ?? index}`}
                        field={column.field}
                        title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
                        cells={{
                          data: (props) => <TemperatureCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                        editable={false}
                        filterable={true}
                        columnMenu={ColumnMenuCheckboxFilter}
                        filter='numeric'
                        sortable={true}
                      />
                      :
                      column.field == 'dateTime' ?
                        <Column
                          key={`synoptic-col-${column.field ?? index}`}
                          field={column.field}
                          title={localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND')}
                          cells={{
                            data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />,
                            headerCell: IconHeaderCell,
                          }}
                          editable={false}
                          columnMenu={(props) => (
                            <GridColumnMenuFilter
                              {...props}
                              alwaysExpand
                              filterUI={CustomGridFilterDateTime}
                              onExpandChange={() => { }}
                              onCloseMenu={() => {
                                const filterName = props.column.field;
                                setFilterName((prevFilterName: any) => {
                                  return prevFilterName.filter((item: any) => item !== filterName);
                                });
                                localStorage.removeItem('filterOpenedColumn');
                              }}
                            />
                          )}
                          filter={'date'}
                          format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                          sortable={true}
                        />
                        :
                        column.field == 'lastPositionDate' ?
                          <Column
                            key={`synoptic-col-${column.field ?? index}`}
                            field={column.field}
                            title={localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND')}
                            cells={{
                              data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} />,
                              headerCell: IconHeaderCell
                            }}
                            editable={false}
                            columnMenu={(props) => (
                              <GridColumnMenuFilter
                                {...props}
                                alwaysExpand
                                filterUI={CustomGridFilterDateTime}
                                onExpandChange={() => { }}
                                onCloseMenu={() => {
                                  const filterName = props.column.field;
                                  setFilterName((prevFilterName: any) => {
                                    return prevFilterName.filter((item: any) => item !== filterName);
                                  });
                                  localStorage.removeItem('filterOpenedColumn');
                                }}
                              />
                            )}
                            filter={'date'}
                            format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                            sortable={true}
                          />
                          :
                          column.field == 'localLastPositionDate' ?
                            <Column
                              key={`synoptic-col-${column.field ?? index}`}
                              field={column.field}
                              title={localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND')}
                              cells={{
                                data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} />,
                                headerCell: IconHeaderCell
                              }}
                              editable={false}
                              //format={column.format}
                              columnMenu={(props) => (
                                <GridColumnMenuFilter
                                  {...props}
                                  alwaysExpand
                                  filterUI={CustomGridFilterDateTime}
                                  onExpandChange={() => { }}
                                  onCloseMenu={() => {
                                    const filterName = props.column.field;
                                    setFilterName((prevFilterName: any) => {
                                      return prevFilterName.filter((item: any) => item !== filterName);
                                    });
                                    localStorage.removeItem('filterOpenedColumn');
                                  }}
                                />
                              )}
                              filter={'date'}
                              format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                              sortable={true}
                            />
                            :
                            column.field == 'localTime' ?
                              <Column
                                key={`synoptic-col-${column.field ?? index}`}
                                field={column.field}
                                title={localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND')}
                                cells={{
                                  data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} />,
                                  headerCell: IconHeaderCell
                                }}
                                editable={false}
                                columnMenu={(props) => (
                                  <GridColumnMenuFilter
                                    {...props}
                                    alwaysExpand
                                    filterUI={CustomGridFilterDateTime}
                                    onExpandChange={() => { }}
                                    onCloseMenu={() => {
                                      const filterName = props.column.field;
                                      setFilterName((prevFilterName: any) => {
                                        return prevFilterName.filter((item: any) => item !== filterName);
                                      });
                                      localStorage.removeItem('filterOpenedColumn');
                                    }}
                                  />
                                )}
                                filter={'date'}
                                format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                                sortable={true}
                              />
                              :
                              column.field == 'customInfo' ?
                                <Column
                                  key={`synoptic-col-${column.field ?? index}`}
                                  field={column.field}
                                  title={localizationService.toLanguageString('synoptic_devices.customInfo', 'NOT_FOUND')}
                                  columnMenu={CustomInfoCheckboxFilter}
                                  editable={false}
                                  filterable={true}
                                  format={column.format}
                                  filter={column.filter}
                                  sortable={true}
                                  cells={{ headerCell: IconHeaderCell }}
                                />
                                :
                                column.field == 'battery' ?
                                  <Column
                                    key={`synoptic-col-${column.field ?? index}`}
                                    field={column.field}
                                    title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
                                    cells={{
                                      data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                                      headerCell: IconHeaderCell
                                    }}
                                    editable={false}
                                    filterable={true}
                                    filter='numeric'
                                    columnMenu={ColumnMenuNumericFilter}
                                    sortable={true}
                                  />
                                  :
                                  column.field == 'status' ?
                                    <Column
                                      key={`synoptic-col-${column.field ?? index}`}
                                      field={column.field}
                                      title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
                                      cells={{
                                        data: (props) => <StatusCellWithColorComponent  {...props} />,
                                        headerCell: IconHeaderCell
                                      }}
                                      editable={false}
                                      filterable={false}
                                      sortable={true}
                                    />
                                    :
                                    column.field == 'inCharge' ?
                                      <Column
                                        key={`synoptic-col-${column.field ?? index}`}
                                        field={column.field}
                                        title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}
                                        cells={{
                                          data: (props) => <InChargeCellWithColorComponent  {...props} />,
                                          headerCell: IconHeaderCell
                                        }}
                                        editable={false}
                                        filterable={false}
                                        sortable={true}
                                      />
                                      :
                                      column.field == 'hasAlarm' ?
                                        <Column
                                          key={`synoptic-col-${column.field ?? index}`}
                                          field={column.field}
                                          title={localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND')}
                                          cells={{
                                            data: (props) => <AlarmCellWithBackGroundComponent  {...props} />,
                                            headerCell: IconHeaderCell
                                          }}
                                          editable={false}
                                          filterable={false}
                                          sortable={true}
                                        />
                                        :
                                        column.field == 'technology' ?
                                          <Column
                                            key={`synoptic-col-${column.field ?? index}`}
                                            field={column.field}
                                            title={localizationService.toLanguageString('synoptic_devices.technology', 'NOT_FOUND')}
                                            cells={{ headerCell: IconHeaderCell }}
                                            columnMenu={ColumnMenuCheckboxFilter}
                                            filterable={true}
                                            filter={column?.filter}
                                            format={column?.format}
                                            editable={column?.editable}
                                            hidden={column?.hidden}
                                          />
                                          :
                                          column.field == 'serialNumber' ?
                                            <Column
                                              key={`synoptic-col-${column.field ?? index}`}
                                              field={column.field}
                                              title={localizationService.toLanguageString('synoptic_devices.serialNumber', 'NOT_FOUND')}
                                              filterable={true}
                                              columnMenu={ColumnMenuCheckboxFilter}
                                              filter={column?.filter}
                                              format={column?.format}
                                              editable={column?.editable}
                                              hidden={column?.hidden}
                                              cells={{
                                                headerCell: IconHeaderCell
                                              }}
                                            />
                                            :
                                            column.field == 'name' ?
                                              <Column
                                                key={`synoptic-col-${column.field ?? index}`}
                                                field={column.field}
                                                title={localizationService.toLanguageString('synoptic_devices.name', 'NOT_FOUND')}
                                                filterable={true}
                                                columnMenu={ColumnMenuCheckboxFilter}
                                                filter={column?.filter}
                                                format={column?.format}
                                                editable={false}
                                                hidden={column?.hidden}
                                                cells={{
                                                  data: NameEditCell,
                                                  headerCell: IconHeaderCell
                                                }}
                                              />
                                              :
                                              column.field == 'zoneNameListJoined' ?
                                                <Column
                                                  key={`synoptic-col-${column.field ?? index}`}
                                                  field={column.field}
                                                  title={localizationService.toLanguageString('synoptic_devices.zoneNameListJoined', 'NOT_FOUND')}
                                                  filterable={true}
                                                  columnMenu={ColumnMenuCheckboxFilter}
                                                  cells={{
                                                    headerCell: IconHeaderCell
                                                  }}
                                                  filter={column?.filter}
                                                  format={column?.format}
                                                  editable={column?.editable}
                                                  hidden={column?.hidden}
                                                />
                                                :
                                                column.field == 'deviceTypeName' ?
                                                  <Column
                                                    key={`synoptic-col-${column.field ?? index}`}
                                                    field={column.field}
                                                    title={localizationService.toLanguageString('synoptic_devices.deviceTypeName', 'NOT_FOUND')}
                                                    filterable={true}
                                                    columnMenu={ColumnMenuCheckboxFilter}
                                                    cells={{
                                                      headerCell: IconHeaderCell
                                                    }}
                                                    filter={column?.filter}
                                                    format={column?.format}
                                                    editable={column?.editable}
                                                    hidden={column?.hidden}
                                                  />
                                                  :
                                                  <Column
                                                    key={`synoptic-col-${column?.field ?? index}`}
                                                    field={column?.field}
                                                    title={column?.title}
                                                    filterable={true}
                                                    columnMenu={ColumnMenuCheckboxFilter}
                                                    filter={column?.filter}
                                                    format={column?.format}
                                                    editable={column?.editable}
                                                    hidden={column?.hidden}
                                                    editor={column?.editor}
                                                    cells={{ headerCell: IconHeaderCell }}
                                                  //width={'2000'}
                                                  />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              columns.show && (
                <Column
                  key={`synoptic-fallback-col-${column?.field ?? index}`}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={ColumnMenuCheckboxFilter}
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  hidden={column?.hidden}
                  editor={column?.editor}
                />
              )
            )
        }
      </Grid>
      <GridPDFExport ref={_pdfExport} margin="1cm">
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column.show && (
                <Column
                  key={`synoptic-pdf-col-${column?.field ?? index}`}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              columns.show && (
                <Column
                  key={`synoptic-pdf-fallback-col-${column?.field ?? index}`}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
        }
        <Grid
          data={devicesOn}
          dataItemKey={"mac"}
          skip={dataState.skip}
          take={dataState.take}
          total={countDevicesOn}
          filterable={false}
          sortable={true}
          reorderable={false}
          pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100], info: true }}
          onColumnReorder={handleColumnReorder}
          onDataStateChange={onDataStateChange}
          onPageChange={onPageChange}
          onItemChange={itemChange}
          edit={edit}
          editable={true}
          scrollable={'none'}
        >
          <GridToolbar style={toolbarStyle} className='synoptic-animated-toolbar synoptic-animated-toolbar-secondary'>
            <div>
                        <h3 className="page-title">{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
            </div>
            <div style={toolbarActionsStyle} className='synoptic-toolbar-actions'>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
              <div>
                <CSVLink data={json2csv(devices)} separator={","}>
                  <Button
                    title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileCsvIcon}
                    style={toolbarButtonStyle}
                    className='synoptic-action-btn'
                  />
                </CSVLink>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                  onClick={pdfExport}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={filePdfIcon}
                  style={toolbarButtonStyle}
                  className='synoptic-action-btn'
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={fileExcelIcon}
                  onClick={excelExport}
                  style={toolbarButtonStyle}
                  className='synoptic-action-btn'
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.save_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={saveIcon}
                  onClick={saveStateToLocalStorage}
                  style={toolbarButtonStyle}
                  className='synoptic-action-btn'
                >
                </Button>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.load_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={uploadIcon}
                  onClick={loadStateFromLocalStorage}
                  style={toolbarButtonStyle}
                  className='synoptic-action-btn'
                >
                </Button>
              </div>
            </div>
          </GridToolbar>
          <Column
            cells={{ data: (props: any) => <EditingCell  {...props} {...commandCellProps} /> }}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkMapCell }}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkDeviceManagement }}
            editable={false}
            filterable={false}
            sortable={false}
          />
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  <Column
                    key={`synoptic-pdf-grid-col-${column?.field ?? index}`}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                columns.show && (
                  <Column
                    key={`synoptic-pdf-grid-fallback-col-${column?.field ?? index}`}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )

          }
          <Column
            field='status'
            title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
            cells={{ data: (props) => <StatusCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={true}
          />
          <Column
            field='groupName'
            title={localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND')}
            cells={{ data: (props) => <GroupCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            columnMenu={ColumnMenuCheckboxFilter}
            sortable={true}
          />
          <Column
            field='jobName'
            title={localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND')}
            cells={{ data: (props) => <JobCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            columnMenu={ColumnMenuCheckboxFilter}
            sortable={true}
          />
          <Column
            field='dateTime'
            title={localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND')}
            cells={{ data: (props) => <DateTimeCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />
          <Column
            field='lastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND')}
            cells={{ data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />
          <Column
            field='localLastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field='localTime'
            title={localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field='temperature'
            title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
            cells={{ data: (props) => <TemperatureCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field='battery'
            title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
            cells={{ data: (props) => <BatteryCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field='inCharge'
            title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}
            cells={{ data: (props) => <InChargeCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={true}
          />
          <Column
            field='alarm'
            title={localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND')}
            cells={{ data: (props) => <AlarmCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={false}
          />
        </Grid>
      </GridPDFExport>
    </div>
  );
};